import React, { useState } from 'react';
import { Box, Drawer, IconButton, List, ListItemButton, ListItemIcon, ListItemText, Tooltip, Typography, useMediaQuery, useTheme } from '@mui/material';
import ChevronLeftIcon from '@mui/icons-material/ChevronLeft';
import MenuIcon from '@mui/icons-material/Menu';
import CloseIcon from '@mui/icons-material/Close';
import { useLocation, useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import menu from '../../config/menu';
import Logo from '../common/Logo';
import { useAuth } from '../../contexts/AuthContext';
import { useCompanyLogo } from '../common/companyLogo';

// Light sidebar, same look as Plankton CRM's NavMenu: white surface, teal icons,
// active item gets a soft teal pill plus a gradient accent bar.
const OPEN_WIDTH = 248;
const CLOSED_WIDTH = 60;
export const MOBILE_TOPBAR_HEIGHT = 56;

const ACTIVE_BG = 'rgba(66,120,152,0.10)';
const HOVER_BG = 'rgba(66,120,152,0.07)';
const TEXT = '#475569';
const ACTIVE_TEXT = '#427898';
const DIVIDER = 'rgba(30,58,82,0.09)';
const ACCENT_GRAD = 'linear-gradient(180deg, #4e90b5 0%, #427898 100%)';

const itemSx = (active, expanded) => ({
    borderRadius: '10px', mb: 0.5, px: expanded ? 1.75 : 0, justifyContent: 'center', minHeight: 42,
    background: active ? ACTIVE_BG : 'transparent', position: 'relative', overflow: 'hidden',
    '&:hover': { background: active ? ACTIVE_BG : HOVER_BG },
    '&::before': active ? {
        content: '""', position: 'absolute', left: 0, top: '18%', height: '64%', width: '3px',
        background: ACCENT_GRAD, borderRadius: '0 3px 3px 0',
    } : {},
});

function NavItems({ expanded, onNavigate }) {
    const { t } = useTranslation();
    const navigate = useNavigate();
    const location = useLocation();
    const { isAdmin } = useAuth();
    const isActive = (item) => location.pathname === item.path
        || (item.match || []).some(m => location.pathname.startsWith(m));

    return (
        <List sx={{ px: 1, pt: 1.5 }}>
            {menu.filter(item => isAdmin || !item.adminOnly).map((item, idx) => {
                if (item.type === 'divider') {
                    return expanded ? (
                        <Typography key={idx} sx={{ px: 1.75, pt: 2, pb: 0.75, fontSize: 11, fontWeight: 700, color: '#94A3B8', textTransform: 'uppercase', letterSpacing: 0.9 }}>
                            {t(item.label)}
                        </Typography>
                    ) : <Box key={idx} sx={{ height: '1px', background: DIVIDER, my: 1.25, mx: 0.5 }} />;
                }
                const active = isActive(item);
                const label = t(item.label);
                return (
                    <Tooltip key={item.path} title={expanded ? '' : label} placement="right" arrow>
                        <ListItemButton onClick={() => { navigate(item.path); onNavigate?.(); }} sx={itemSx(active, expanded)}>
                            <ListItemIcon sx={{ color: '#427898', minWidth: expanded ? 36 : 'unset', justifyContent: 'center' }}>
                                {item.icon}
                            </ListItemIcon>
                            {expanded && (
                                <ListItemText primary={label} primaryTypographyProps={{
                                    fontSize: 13.5, fontWeight: active ? 600 : 500, color: active ? ACTIVE_TEXT : TEXT,
                                }} />
                            )}
                        </ListItemButton>
                    </Tooltip>
                );
            })}
        </List>
    );
}

function CompanyBadge({ expanded }) {
    const { user } = useAuth();
    const logo = useCompanyLogo();
    if (!user) return null;
    const initials = (user.companyName || '?').split(/\s+/).map(w => w[0]).slice(0, 2).join('').toUpperCase();
    return (
        <Box sx={{ p: 1.25, borderTop: `1px solid ${DIVIDER}` }}>
            <Tooltip title={expanded ? '' : user.companyName} placement="right" arrow>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.25, p: expanded ? 1 : 0.5, justifyContent: expanded ? 'flex-start' : 'center', borderRadius: '12px', bgcolor: expanded ? 'rgba(66,120,152,0.05)' : 'transparent' }}>
                    {/* The firm's own logo when uploaded (Company page), otherwise its initials. */}
                    <Box sx={{ width: 32, height: 32, borderRadius: '9px', bgcolor: logo ? '#fff' : 'rgba(66,120,152,0.14)', color: '#427898', fontWeight: 700, fontSize: 12.5,
                               display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0, overflow: 'hidden', border: logo ? '1px solid #E2E8F0' : 0 }}>
                        {logo ? <Box component="img" src={logo} alt="" sx={{ maxWidth: '88%', maxHeight: '88%', objectFit: 'contain' }} /> : initials}
                    </Box>
                    {expanded && (
                        <Box sx={{ minWidth: 0 }}>
                            <Typography noWrap sx={{ fontSize: 13, fontWeight: 700, color: '#1a2332' }}>{user.companyName}</Typography>
                            <Typography noWrap sx={{ fontSize: 11.5, color: '#94A3B8' }}>{user.firstName} {user.lastName}</Typography>
                        </Box>
                    )}
                </Box>
            </Tooltip>
        </Box>
    );
}

export default function NavMenu() {
    const theme = useTheme();
    const isMobile = useMediaQuery(theme.breakpoints.down('md'));
    const [open, setOpen] = useState(() => localStorage.getItem('SD_NAV_OPEN') !== 'false');
    const [mobileOpen, setMobileOpen] = useState(false);
    const { t } = useTranslation();

    const toggle = () => {
        localStorage.setItem('SD_NAV_OPEN', String(!open));
        setOpen(!open);
    };

    if (isMobile) {
        return (
            <>
                <Box sx={{
                    position: 'fixed', top: 0, left: 0, right: 0, height: MOBILE_TOPBAR_HEIGHT, bgcolor: '#fff',
                    borderBottom: `1px solid ${DIVIDER}`, display: 'flex', alignItems: 'center', justifyContent: 'space-between',
                    px: 2, zIndex: 1100,
                }}>
                    <Logo size={28} />
                    <IconButton onClick={() => setMobileOpen(true)} sx={{ color: '#427898' }} aria-label={t('a11y.openMenu')}><MenuIcon /></IconButton>
                </Box>
                <Drawer anchor="left" open={mobileOpen} onClose={() => setMobileOpen(false)}
                        PaperProps={{ sx: { width: OPEN_WIDTH, display: 'flex', flexDirection: 'column' } }}>
                    <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', px: 2, height: 64, borderBottom: `1px solid ${DIVIDER}` }}>
                        <Logo size={28} />
                        <IconButton size="small" onClick={() => setMobileOpen(false)} sx={{ color: '#427898' }} aria-label={t('a11y.closeMenu')}><CloseIcon fontSize="small" /></IconButton>
                    </Box>
                    <Box sx={{ flexGrow: 1, overflowY: 'auto' }}><NavItems expanded onNavigate={() => setMobileOpen(false)} /></Box>
                    <CompanyBadge expanded />
                </Drawer>
            </>
        );
    }

    const width = open ? OPEN_WIDTH : CLOSED_WIDTH;
    return (
        <Drawer variant="permanent" sx={{
            width, flexShrink: 0, transition: 'width 0.3s ease',
            '& .MuiDrawer-paper': {
                width, boxSizing: 'border-box', bgcolor: '#fff', borderRight: '1px solid #e6ecf1',
                boxShadow: '4px 0 24px -12px rgba(30,58,82,0.12)', transition: 'width 0.3s ease', overflow: 'hidden',
                display: 'flex', flexDirection: 'column',
            },
        }}>
            <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: open ? 'space-between' : 'center', px: open ? 2.25 : 1, height: 64, flexShrink: 0, borderBottom: `1px solid ${DIVIDER}` }}>
                {open && <Logo size={30} />}
                <IconButton onClick={toggle} size="small" aria-label={t('a11y.toggleSidebar')} sx={{
                    width: 30, height: 30, bgcolor: 'rgba(66,120,152,0.10)', color: '#427898',
                    transform: open ? 'none' : 'rotate(180deg)', transition: 'transform 0.3s ease',
                    '&:hover': { bgcolor: 'rgba(66,120,152,0.18)' },
                }}>
                    <ChevronLeftIcon fontSize="small" />
                </IconButton>
            </Box>
            <Box sx={{ flexGrow: 1, overflowY: 'auto', overflowX: 'hidden' }}><NavItems expanded={open} /></Box>
            <CompanyBadge expanded={open} />
        </Drawer>
    );
}
